import { round } from 'lodash';
import getNiceStepCeil from '../../../utils/get-nice-step-ceil';
import { BridgeChartRow } from '../types';
import { getMaxColumnTop, getMiddleBridgeExtents } from './bridge-extents';
import {
  DEFAULT_Y_AXIS_STEP,
  POSITIVE_Y_AXIS_STEP_COUNT,
  VALUE_BAND_BELOW_NEGATIVE_RATIO,
} from './constants';

const ensureStepClearsColumnTops = (
  referenceLineValue: number,
  step: number,
  maxColumnTop: number,
  positiveStepCount: number,
) => {
  let adjustedStep = step;

  if (referenceLineValue + positiveStepCount * adjustedStep <= maxColumnTop) {
    adjustedStep =
      getNiceStepCeil(
        (maxColumnTop - referenceLineValue) / positiveStepCount,
      ) || adjustedStep;
  }

  if (referenceLineValue + positiveStepCount * adjustedStep <= maxColumnTop) {
    adjustedStep = round(adjustedStep + 0.01, 2);
  }

  return adjustedStep;
};

const buildStandardBridgeYAxisLayout = ({
  referenceLineValue,
  step: initialStep,
  maxColumnTop,
  usesBridgeFocusedAxis,
  hasNegativeValues,
  dataMin,
}: {
  referenceLineValue: number;
  step: number;
  maxColumnTop: number;
  usesBridgeFocusedAxis: boolean;
  hasNegativeValues: boolean;
  dataMin?: number;
}) => {
  const positiveStepCount = hasNegativeValues
    ? POSITIVE_Y_AXIS_STEP_COUNT
    : POSITIVE_Y_AXIS_STEP_COUNT + 1;
  const step = ensureStepClearsColumnTops(
    referenceLineValue,
    initialStep,
    maxColumnTop,
    positiveStepCount,
  );
  const displayedNegativeTick = round(referenceLineValue - step, 2);
  const valueBandStep = round(step * VALUE_BAND_BELOW_NEGATIVE_RATIO, 2);
  const valueBandTop = hasNegativeValues
    ? displayedNegativeTick
    : referenceLineValue;
  const valueBandFloor = hasNegativeValues
    ? round(displayedNegativeTick - valueBandStep, 2)
    : round(referenceLineValue - valueBandStep, 2);
  const positiveTicks = Array.from({ length: positiveStepCount }, (_, index) =>
    round(referenceLineValue + (index + 1) * step, 2),
  );
  const topGridLine =
    positiveTicks[positiveTicks.length - 1] ?? referenceLineValue;
  const ticks = (
    hasNegativeValues
      ? [
          valueBandFloor,
          displayedNegativeTick,
          referenceLineValue,
          ...positiveTicks,
        ]
      : [valueBandFloor, referenceLineValue, ...positiveTicks]
  ).filter((tick, index, array) => array.indexOf(tick) === index);
  const domainMin =
    dataMin !== undefined ? Math.min(valueBandFloor, dataMin) : valueBandFloor;
  const domainMax = round(Math.max(topGridLine, maxColumnTop + 0.01), 2);
  const domain: [number, number] = [domainMin, domainMax];

  const hiddenTickValues = [valueBandFloor];
  const horizontalGridValues = hasNegativeValues
    ? ticks.filter((tick) => tick !== referenceLineValue)
    : ticks;

  return {
    domain,
    ticks,
    horizontalGridValues,
    hiddenTickValues,
    referenceLineValue,
    showReferenceLine: hasNegativeValues,
    usesBridgeFocusedAxis,
    step,
    valueBandStep,
    valueBandTop,
    valueBandFloor,
    yAxisLabelOffset: 0,
  };
};

export const buildAbsoluteBridgeYAxis = (chartRows: Array<BridgeChartRow>) => {
  const middleRows = chartRows.filter((row) => !row.isTotal);
  const totalRows = chartRows.filter((row) => row.isTotal);
  const middleStackTops = middleRows.map((row) =>
    round(row.base + row.value, 2),
  );
  const middleStackBottoms = middleRows.map((row) => row.base);
  const totalStackTops = totalRows.map((row) => round(row.base + row.value, 2));

  const maxPositive = Math.max(0, ...middleStackTops, ...totalStackTops);
  const minNegative = Math.min(0, ...middleStackBottoms);

  const { maxMiddleDelta } = getMiddleBridgeExtents(middleRows);

  const hasNegativeValues = minNegative < 0;
  const positiveStepCount = hasNegativeValues
    ? POSITIVE_Y_AXIS_STEP_COUNT
    : POSITIVE_Y_AXIS_STEP_COUNT + 1;
  const positiveExtent = Math.max(maxPositive, maxMiddleDelta);
  const step =
    positiveExtent > 0
      ? getNiceStepCeil(positiveExtent / positiveStepCount)
      : DEFAULT_Y_AXIS_STEP;

  return buildStandardBridgeYAxisLayout({
    referenceLineValue: 0,
    step,
    maxColumnTop: maxPositive,
    usesBridgeFocusedAxis: false,
    hasNegativeValues,
    dataMin: minNegative < -step ? minNegative : undefined,
  });
};

export const buildBridgeFocusedYAxis = (chartRows: Array<BridgeChartRow>) => {
  const middleRows = chartRows.filter((row) => !row.isTotal);
  const totalRows = chartRows.filter((row) => row.isTotal);
  const { bridgeMin, maxMiddleDelta } = getMiddleBridgeExtents(middleRows);
  const totalBarTops = totalRows.map((row) => round(row.base + row.value, 2));
  const maxTotalBarTop =
    totalBarTops.length > 0 ? Math.max(...totalBarTops) : 0;
  const hasNegativeValues = bridgeMin < 0;
  const positiveStepCount = hasNegativeValues
    ? POSITIVE_Y_AXIS_STEP_COUNT
    : POSITIVE_Y_AXIS_STEP_COUNT + 1;
  const stepBasis = Math.max(maxMiddleDelta, maxTotalBarTop - bridgeMin, 0);

  const step =
    stepBasis > 0
      ? getNiceStepCeil(stepBasis / positiveStepCount)
      : DEFAULT_Y_AXIS_STEP;

  const axisBaseline = round(Math.max(0, bridgeMin - step), 2);

  return buildStandardBridgeYAxisLayout({
    referenceLineValue: axisBaseline,
    step,
    maxColumnTop: getMaxColumnTop(chartRows),
    usesBridgeFocusedAxis: true,
    hasNegativeValues,
    dataMin: bridgeMin < axisBaseline - step ? bridgeMin : undefined,
  });
};
